import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useTheme } from "../contexts/ThemeContext";
import AccountModal from "../components/AccountModal";
import "./LotDashboard.css";
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useQuery } from '@tanstack/react-query';
import { devicesService, notificationService, lotService } from '../utils/api';
import Pagination from "../components/Pagination";
import { usePagination } from "../hooks/usePagination";
import { PaginationParams, PageResponse, UserLot } from "../types";
import {
    isSuperadminModeEnabled,
    setSuperadminModeEnabled,
    SUPERADMIN_MODE_EVENT,
    SUPERADMIN_MODE_STORAGE_KEY,
} from "../components/SuperadminHeader";

const useLotDevices = (lotId: string) => {
    return useQuery({
        queryKey: ['devices', lotId],
        queryFn: () => devicesService.getDevicesByLot(lotId),
        staleTime: 5 * 60 * 1000,
    });
};

const LotDashboard = () => {
    const navigate = useNavigate();
    const { user, userLots, loading: userLoading, error, fetchUserLots, logout } = useUser();
    const { isDarkMode } = useTheme();

    const [searchQuery, setSearchQuery] = useState("");
    const [showAccountModal, setShowAccountModal] = useState(false);
    const [isSuperadminModeOn, setIsSuperadminModeOn] = useState<boolean>(() => isSuperadminModeEnabled());
    const [viewMode, setViewMode] = useState<"grid" | "list">(() => {
        const savedViewMode = localStorage.getItem("lotDashboardViewMode");
        return (savedViewMode === "list" || savedViewMode === "grid") ? savedViewMode : "grid";
    });

    // Mobile detection
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const [showArchivedLots, setShowArchivedLots] = useState<boolean>(false);

    const accountRole = (user?.role || "").toLowerCase();
    const isAccountSuperadmin = accountRole === "superadmin";
    const isUsingSuperadminLots = isAccountSuperadmin && isSuperadminModeOn;
    const canOpenDisabledLots = isAccountSuperadmin && isSuperadminModeOn;

    const getHeaderLogoSrc = () => {
        if (isAccountSuperadmin && isSuperadminModeOn) {
            return "/assets/superadmin/logo_superadmin.svg";
        }
        return "/assets/logos/Logo_Operator_Inline.svg";
    };

    const renderHeaderLogo = () => (
        <img
            src={getHeaderLogoSrc()}
            alt="Logo"
            className={`logo-owner${isAccountSuperadmin && isSuperadminModeOn ? " superadmin" : ""}`}
        />
    );

    useEffect(() => {
        if (!isAccountSuperadmin) {
            setSuperadminModeEnabled(false);
            setIsSuperadminModeOn(false);
            return;
        }

        setIsSuperadminModeOn(isSuperadminModeEnabled());
    }, [isAccountSuperadmin]);

    useEffect(() => {
        const handleModeChange = (event: Event) => {
            const detail = (event as CustomEvent<{ enabled: boolean }>).detail;
            setIsSuperadminModeOn(detail.enabled);
        };

        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === SUPERADMIN_MODE_STORAGE_KEY) {
                setIsSuperadminModeOn(event.newValue === "true");
            }
        };

        window.addEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener(SUPERADMIN_MODE_EVENT, handleModeChange as EventListener);
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);

    const normalizeLotForDashboard = useCallback(
        (lot: any) => ({
            lotId: lot.lotId,
            displayLotId: lot.displayLotId || lot.lotId,
            lotName: lot.lotName || "Untitled Lot",
            address: lot.address || "No address available",
            companyName: lot.companyName || "",
            lotCapacity: typeof lot.lotCapacity === "number" ? lot.lotCapacity : Number(lot.lotCapacity) || 0,
            currentOccupancy:
                typeof lot.currentOccupancy === "number"
                    ? lot.currentOccupancy
                    : Number(lot.currentOccupancy) || 0,
            createdOn: lot.createdOn,
            lotStatus: lot.lotStatus || "Active",
            lotLatitude: lot.lotLatitude ?? lot.latitude ?? null,
            lotLongitude: lot.lotLongitude ?? lot.longitude ?? null,
            developmentLot: lot.developmentLot === true,
        }),
        []
    );

    const fetchSuperadminLotsPage = useCallback(
        async (params: PaginationParams): Promise<PageResponse<any>> => {
            const response = await lotService.getAllLotsOffsetPaginated({
                ...params,
                sortBy: params.sortBy || "lotId",
                sortDirection: params.sortDirection || "asc",
                includeArchived: showArchivedLots,
            } as PaginationParams);
            const content = (response.content || []).map((lot: any) => normalizeLotForDashboard(lot));
            return {
                ...response,
                content,
            };
        },
        [normalizeLotForDashboard, showArchivedLots]
    );

    const fetchUserLotsPage = useCallback(
        async (params: PaginationParams): Promise<PageResponse<any>> => {
            const lots = await fetchUserLots();
            const filtered = lots.filter((lot: UserLot) => {
                const normalizedStatus = (lot.lotStatus || "").toLowerCase();
                if (normalizedStatus === "suspended" || normalizedStatus === "archived") {
                    return false;
                }

                if (!params.search) return true;
                const searchable = `${lot.displayLotId || ""} ${lot.companyName || ""} ${lot.lotName || ""} ${lot.address || ""} ${lot.createdOn || ""
                    }`.toLowerCase();
                return searchable.includes(params.search.toLowerCase());
            });

            const sorted = [...filtered].sort((a, b) => {
                const field = params.sortBy || "lotId";
                const valueA = (a as any)[field];
                const valueB = (b as any)[field];

                if (field === "createdOn") {
                    const dateA = valueA ? new Date(valueA as string).getTime() : 0;
                    const dateB = valueB ? new Date(valueB as string).getTime() : 0;
                    return (params.sortDirection || "asc") === "asc" ? dateA - dateB : dateB - dateA;
                }

                const stringA = valueA ? valueA.toString().toLowerCase() : "";
                const stringB = valueB ? valueB.toString().toLowerCase() : "";
                const comparison = stringA.localeCompare(stringB);
                return (params.sortDirection || "asc") === "asc" ? comparison : -comparison;
            });

            const startIndex = params.page * params.size;
            const paged = sorted.slice(startIndex, startIndex + params.size).map(normalizeLotForDashboard);
            const totalElements = sorted.length;
            const totalPages = Math.ceil(totalElements / params.size);

            return {
                content: paged,
                page: params.page,
                size: params.size,
                totalElements,
                totalPages,
                first: params.page === 0,
                last: params.page >= totalPages - 1,
                sortBy: params.sortBy,
                sortDirection: params.sortDirection,
            };
        },
        [fetchUserLots, normalizeLotForDashboard]
    );

    const fetchLots = useCallback(
        (params: PaginationParams) =>
            isUsingSuperadminLots ? fetchSuperadminLotsPage(params) : fetchUserLotsPage(params),
        [isUsingSuperadminLots, fetchSuperadminLotsPage, fetchUserLotsPage]
    );

    const {
        items: paginatedLots,
        totalElements,
        totalPages,
        page,
        loading: paginationLoading,
        sortBy,
        sortDirection,
        error: paginationError,
        goToPage,
        setSort,
        setSearch,
        refresh: refreshPagination,
    } = usePagination<any>({
        fetchFn: fetchLots,
        initialPage: 0,
        initialSize: 10,
        initialSortBy: "lotId",
        initialSortDirection: "asc",
        enabled: isUsingSuperadminLots || !!user?.userId,
    });

    useEffect(() => {
        if (isUsingSuperadminLots) {
            goToPage(0);
            refreshPagination();
        } else {
            setShowArchivedLots(false);
        }
    }, [isUsingSuperadminLots, showArchivedLots, goToPage, refreshPagination]);

    const handleSuperadminToggle = () => {
        if (!isAccountSuperadmin) {
            return;
        }
        setSuperadminModeEnabled(!isSuperadminModeOn);
    };

    // Fetch lots when component mounts if not already loaded
    useEffect(() => {
        if (userLots.length === 0) {
            fetchUserLots();
        }
    }, [fetchUserLots, userLots]);

    // Clear timeframe keys from localStorage when LotDashboard is visited
    useEffect(() => {
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith("timeframe_")) {
                localStorage.removeItem(key);
            }
        });
    }, []);

    const handleLogout = async () => {
        // Clear timeframe keys before logging out
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith("timeframe_")) {
                localStorage.removeItem(key);
            }
        });
        await logout();
    };

    const handleViewModeChange = (newViewMode: "grid" | "list") => {
        setViewMode(newViewMode);
        localStorage.setItem("lotDashboardViewMode", newViewMode);
    };

    const handleSort = (column: string) => {
        setSort(column);
    };

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        setSearch(value);
        goToPage(0);
    };

    // Format date strings consistently to match SuperAdmin dashboard
    const formatDate = (isoDate: string): string => {
        if (!isoDate) return 'N/A';
        return new Date(isoDate).toLocaleString();
    };

    const currentItems = paginatedLots;
    const totalItems = totalElements;

    // Helper function to format the lot ID using displayLotId field
    const formatLotId = (lot: any): string => {
        if (!lot.displayLotId) return 'N/A';
        const last8 = lot.displayLotId.slice(-8);
        return `${last8.slice(0, 4)}-${last8.slice(4)}`;
    };

    const buildLotNavigationPath = (lotId: string) => {
        const base = `/lot/${lotId}/dashboard/revenue`;
        return isUsingSuperadminLots ? `${base}?superadmin=true` : base;
    };

    // Helper for device status dots
    const DeviceStatusDots: React.FC<{ lotId: string; developmentLot?: boolean }> = ({ lotId, developmentLot }) => {
        const { data: devices = [] } = useLotDevices(lotId);

        if (!devices) return null;

        if (developmentLot) {
            const dots = [];
            for (let i = 0; i < devices.length; i++) {
                dots.push(<span key={'on' + i} className="device-dot online" />);
            }
            return <span className="device-status-dots">{dots}</span>;
        }

        const online = devices.filter((d: any) => d.deviceStatus === 'Online').length;
        const recentlyAdded = devices.filter((d: any) => d.deviceStatus === 'RecentlyAdded').length;
        const offline = devices.filter((d: any) => d.deviceStatus === 'Offline').length;

        const dots = [];
        for (let i = 0; i < online; i++) dots.push(<span key={'on' + i} className="device-dot online" />);
        for (let i = 0; i < recentlyAdded; i++) dots.push(<span key={'ra' + i} className="device-dot recently-added" />);
        for (let i = 0; i < offline; i++) dots.push(<span key={'off' + i} className="device-dot offline" />);
        return <span className="device-status-dots">{dots}</span>;
    };

    // Helper for notification count
    const NotificationCount = ({ lotId }: { lotId: string }) => {
        const { data: count, isLoading, error } = useQuery({
            queryKey: ['notificationCount', lotId],
            queryFn: () => notificationService.countUnreadNotificationsByLot(lotId),
            staleTime: 30000, // 30 seconds
            retry: 1, // Only retry once to avoid excessive API calls
        });

        if (isLoading) {
            return <span className="notification-count">Loading...</span>;
        }

        if (error) {
            return <span className="notification-count">0</span>;
        }

        // If count is -1, it means the endpoint doesn't exist yet (backend not deployed)
        if (count === -1) {
            return <span className="notification-count">COMING SOON</span>;
        }

        return <span className="notification-count">{count} Notifications</span>;
    };

    // Helper for map marker icon
    const blueDot = new L.DivIcon({
        html: '<div class="map-marker-blue"></div>',
        className: '',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
    });
    const grayDot = new L.DivIcon({
        html: '<div class="map-marker-gray"></div>',
        className: '',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
    });

    // Helper function to format purchase date as text
    const formatPurchaseDate = (isoDate: string): string => {
        if (!isoDate) return 'N/A';
        const date = new Date(isoDate);
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };
        return `Since ${date.toLocaleDateString('en-US', options)}`;
    };

    // LotCard component
    const LotCard: React.FC<{ lot: any }> = ({ lot }) => {
        const lotIdFormatted = lot.displayLotId ? `${lot.displayLotId.slice(-8, -4)}-${lot.displayLotId.slice(-4)}` : lot.lotId;
        const purchaseDate = formatPurchaseDate(lot.createdOn);
        const occ = typeof lot.currentOccupancy === 'number' ? lot.currentOccupancy : 0;
        const cap = typeof lot.lotCapacity === 'number' ? lot.lotCapacity : 0;
        const hasCoords = typeof lot.lotLatitude === 'number' && typeof lot.lotLongitude === 'number' && !isNaN(lot.lotLatitude) && !isNaN(lot.lotLongitude);
        const occupancyPercentage = cap > 0 ? Math.min(100, Math.round((occ / cap) * 100)) : 0;
        const normalizedStatus = (lot.lotStatus || "").toLowerCase();
        const isStatusRestricted =
            normalizedStatus === "paused" || normalizedStatus === "suspended" || normalizedStatus === "archived";
        const isDisabledLot = !canOpenDisabledLots && isStatusRestricted;

        const handleCardClick = isDisabledLot ? undefined : () => navigate(buildLotNavigationPath(lot.lotId));

        return (
            <div
                className={`lot-card ${isStatusRestricted ? 'paused-lot' : 'clickable'}`}
                onClick={handleCardClick}
                style={{
                    opacity: isStatusRestricted ? 0.5 : 1,
                    cursor: isDisabledLot ? 'not-allowed' : 'pointer'
                }}
            >
                <div className="lot-card-content">
                    <div className="lot-card-header">
                        <div className="lot-card-title-group">
                            <div className="lot-card-title ellipsis">{lot.lotName}</div>
                            {lot.developmentLot ? (
                                <span className="lot-card-id demo">Demo</span>
                            ) : (
                                <span className="lot-card-id">{lotIdFormatted}</span>
                            )}
                        </div>
                        <DeviceStatusDots lotId={lot.lotId} developmentLot={lot.developmentLot} />
                    </div>
                    <div className="lot-card-info-row">
                        <img src="/assets/lot_dashboard/lotcard_company.svg" alt="company" className="lot-card-icon" />
                        <span className="ellipsis">{lot.companyName || 'Company Name'}</span>
                    </div>
                    <div className="lot-card-info-row">
                        <img src="/assets/lot_dashboard/lotcard_location.svg" alt="address" className="lot-card-icon" />
                        <span className="ellipsis">{lot.address}</span>
                    </div>
                    {!isMobile && (
                        <div className="lot-card-info-row">
                            <img src="/assets/lot_dashboard/lotcard_purchased.svg" alt="since" className="lot-card-icon" />
                            <span className="ellipsis">{purchaseDate}</span>
                        </div>
                    )}
                    <div className="lot-card-info-row">
                        <img src="/assets/lot_dashboard/lotcard_notification.svg" alt="notifications" className="lot-card-icon" />
                        <NotificationCount lotId={lot.lotId} />
                    </div>
                    <div className="lot-card-progress-container">
                        <div className="lot-card-progress-bar">
                            <div className="lot-card-progress-fill" style={{ width: `${occupancyPercentage}%` }}></div>
                        </div>
                        <div className="lot-card-occupancy-count">
                            {occ}/{cap}
                        </div>
                    </div>
                </div>
                <div className="lot-card-map-container">
                    {hasCoords ? (
                        <MapContainer
                            center={[lot.lotLatitude, lot.lotLongitude]}
                            zoom={14}
                            className="map-container"
                            dragging={isMobile ? false : false}
                            scrollWheelZoom={false}
                            doubleClickZoom={false}
                            zoomControl={false}
                            attributionControl={false}
                            keyboard={false}
                            touchZoom={!isMobile}
                        >
                            <TileLayer
                                url={isDarkMode
                                    ? "https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png"
                                    : "https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                                }
                                subdomains="abcd"
                                maxZoom={30}
                            />
                            <Marker position={[lot.lotLatitude, lot.lotLongitude]} icon={blueDot} />
                        </MapContainer>
                    ) : (
                        <div className="lot-card-map-placeholder">
                            <div className="lot-card-map-placeholder-dot"></div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Show loading state
    if (userLoading || paginationLoading) {
        return <div className="loading-state">Loading lots...</div>;
    }

    // Show error state
    if (error || paginationError) {
        return <div className="error-state">Error: {error || paginationError}</div>;
    }

    // Show empty state if no lots found
    if (!isUsingSuperadminLots && totalItems === 0) {
        return (
            <div className="dashboard">
                <div className="lot-dashboard-header">
                    <div className="header-content">
                        <div className="header-left">
                            {renderHeaderLogo()}
                            {isAccountSuperadmin && (
                                <button
                                    type="button"
                                    className={`superadmin-mode-button ${isSuperadminModeOn ? "active" : ""}`}
                                    onClick={handleSuperadminToggle}
                                    aria-label={isSuperadminModeOn ? "Disable superadmin mode" : "Enable superadmin mode"}
                                    title={isSuperadminModeOn ? "Disable superadmin mode" : "Enable superadmin mode"}
                                >
                                    <img
                                        src={
                                            isSuperadminModeOn
                                                ? "/assets/superadmin/superadmin_disable.svg"
                                                : "/assets/superadmin/superadmin_enable.svg"
                                        }
                                        alt=""
                                        aria-hidden="true"
                                    />
                                </button>
                            )}
                        </div>
                        <div className="header-actions">
                            {!isMobile && (
                                <button className="icon-button account" onClick={() => setShowAccountModal(true)}>
                                    <img src="/assets/nav/Account.svg" alt="Account" />
                                    <span>Account</span>
                                </button>
                            )}
                            <button className="icon-button logout" onClick={handleLogout}>
                                <img src="/assets/nav/Logout.svg" alt="Logout" />
                                {!isMobile && <span>Logout</span>}
                            </button>
                        </div>
                    </div>
                </div>
                <div className="lot-dashboard-content">
                    <div className="search-and-view-container">
                        <div className="search-bar">
                            <img src="/assets/SearchBarIcon.svg" alt="Search" />
                                <input
                                    type="text"
                                    placeholder={isMobile ? "Search Lot Info..." : "Search LotID, Name, or Location"}
                                    value={searchQuery}
                                    onChange={(e) => handleSearchChange(e.target.value)}
                                />
                            </div>
                        <div className="view-toggle-buttons">
                            <button
                                className={`view-toggle-btn ${viewMode === 'grid' ? 'selected' : ''}`}
                                onClick={() => handleViewModeChange('grid')}
                            >
                                <img
                                    src={viewMode === 'grid' ? "/assets/lot_dashboard/grid_view_selected.svg" : "/assets/lot_dashboard/grid_view.svg"}
                                    alt="Grid View"
                                />
                            </button>
                            <button
                                className={`view-toggle-btn ${viewMode === 'list' ? 'selected' : ''}`}
                                onClick={() => handleViewModeChange('list')}
                            >
                                <img
                                    src={viewMode === 'list' ? "/assets/lot_dashboard/list_view_selected.svg" : "/assets/lot_dashboard/list_view.svg"}
                                    alt="List View"
                                />
                            </button>
                        </div>
                    </div>
                    <div className="empty-state">
                        <h2>No Lots Found</h2>
                        <p>You don't have any lots assigned to your account.</p>
                    </div>
                </div>
                <AccountModal isOpen={showAccountModal} onClose={() => setShowAccountModal(false)} />
            </div>
        );
    }

    return (
        <div className="dashboard">
            {/* Header */}
            <div className="lot-dashboard-header">
                <div className="header-content">
                    <div className="header-left">
                        {renderHeaderLogo()}
                        {isAccountSuperadmin && (
                            <button
                                type="button"
                                className={`superadmin-mode-button ${isSuperadminModeOn ? "active" : ""}`}
                                onClick={handleSuperadminToggle}
                                aria-label={isSuperadminModeOn ? "Disable superadmin mode" : "Enable superadmin mode"}
                                title={isSuperadminModeOn ? "Disable superadmin mode" : "Enable superadmin mode"}
                            >
                                <img
                                    src={
                                        isSuperadminModeOn
                                            ? "/assets/superadmin/superadmin_disable.svg"
                                            : "/assets/superadmin/superadmin_enable.svg"
                                    }
                                    alt=""
                                    aria-hidden="true"
                                />
                            </button>
                        )}
                    </div>
                    <div className="header-actions">
                        {!isMobile && (
                            <button className="icon-button account" onClick={() => setShowAccountModal(true)}>
                                <img src="/assets/nav/Account.svg" alt="Account" />
                                <span>{user?.name || 'Loading...'}</span>
                            </button>
                        )}
                        <button className="icon-button logout" onClick={handleLogout}>
                            <img src="/assets/nav/Logout.svg" alt="Logout" />
                            {!isMobile && <span>Logout</span>}
                        </button>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="lot-dashboard-content">
                <div className="search-and-view-container">
                    <div className="search-bar">
                        <img src="/assets/SearchBarIcon.svg" alt="Search" />
                            <input
                                type="text"
                                placeholder="Search LotID, Name, or Location"
                                value={searchQuery}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                    </div>
                    <div className="search-action-group">
                        {!isMobile && (
                            <div className="view-toggle-buttons">
                                <button
                                    className={`view-toggle-btn ${viewMode === 'grid' ? 'selected' : ''}`}
                                    onClick={() => handleViewModeChange('grid')}
                                >
                                    <img
                                        src={viewMode === 'grid' ? "/assets/lot_dashboard/grid_view_selected.svg" : "/assets/lot_dashboard/grid_view.svg"}
                                        alt="Grid View"
                                    />
                                </button>
                                <button
                                    className={`view-toggle-btn ${viewMode === 'list' ? 'selected' : ''}`}
                                    onClick={() => handleViewModeChange('list')}
                                >
                                    <img
                                        src={viewMode === 'list' ? "/assets/lot_dashboard/list_view_selected.svg" : "/assets/lot_dashboard/list_view.svg"}
                                        alt="List View"
                                    />
                                </button>
                            </div>
                        )}
                        {isUsingSuperadminLots && (
                            <label className="show-archived-toggle">
                                <span className="show-archived-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={showArchivedLots}
                                        onChange={() => setShowArchivedLots((prev) => !prev)}
                                        aria-label="Toggle archived lots"
                                    />
                                    <span className="show-archived-checkbox-styler" />
                                </span>
                                <span>Show Archived</span>
                            </label>
                        )}
                    </div>
                </div>

                {/* Grid View */}
                {viewMode === 'grid' && (
                    <div className="grid-view-container">
                        {currentItems.map((lot) => (
                            <LotCard key={lot.lotId} lot={lot} />
                        ))}
                    </div>
                )}

                {/* List View - Table */}
                {viewMode === 'list' && (
                    <>
                        <div className="table-container lot-dashboard-table-container">
                            <table className="dashboard-table app-table app-table-clickable">
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort("lotId")} className={`sortable-column ${sortBy === "lotId" ? "active" : ""}`}>
                                            Lot ID
                                            <img
                                                src={`/assets/tables/${sortBy === "lotId" ? "SortArrowSelected.svg" : "SortArrow.svg"}`}
                                                alt="Sort"
                                                className={`sort-arrow ${sortDirection}`}
                                            />
                                        </th>
                                        <th onClick={() => handleSort("lotName")} className={`sortable-column ${sortBy === "lotName" ? "active" : ""}`}>
                                            Lot Name
                                            <img
                                                src={`/assets/tables/${sortBy === "lotName" ? "SortArrowSelected.svg" : "SortArrow.svg"}`}
                                                alt="Sort"
                                                className={`sort-arrow ${sortDirection}`}
                                            />
                                        </th>
                                        <th onClick={() => handleSort("companyName")} className={`sortable-column ${sortBy === "companyName" ? "active" : ""}`}>
                                            Company Name
                                            <img
                                                src={`/assets/tables/${sortBy === "companyName" ? "SortArrowSelected.svg" : "SortArrow.svg"}`}
                                                alt="Sort"
                                                className={`sort-arrow ${sortDirection}`}
                                            />
                                        </th>
                                        <th onClick={() => handleSort("address")} className={`sortable-column ${sortBy === "address" ? "active" : ""}`}>
                                            Address
                                            <img
                                                src={`/assets/tables/${sortBy === "address" ? "SortArrowSelected.svg" : "SortArrow.svg"}`}
                                                alt="Sort"
                                                className={`sort-arrow ${sortDirection}`}
                                            />
                                        </th>
                                        <th>
                                            Cameras
                                        </th>
                                        <th onClick={() => handleSort("createdOn")} className={`sortable-column ${sortBy === "createdOn" ? "active" : ""}`}>
                                            Purchase Date
                                            <img
                                                src={`/assets/tables/${sortBy === "createdOn" ? "SortArrowSelected.svg" : "SortArrow.svg"}`}
                                                alt="Sort"
                                                className={`sort-arrow ${sortDirection}`}
                                            />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentItems.map((lot) => {
                                        const normalizedStatus = (lot.lotStatus || "").toLowerCase();
                                        const isStatusRestricted =
                                            normalizedStatus === "paused" ||
                                            normalizedStatus === "suspended" ||
                                            normalizedStatus === "archived";
                                        const isDisabledLot = !canOpenDisabledLots && isStatusRestricted;
                                        const handleRowClick = isDisabledLot
                                            ? undefined
                                            : () => navigate(buildLotNavigationPath(lot.lotId));

                                        return (
                                            <tr
                                                key={lot.lotId}
                                                onClick={handleRowClick}
                                                className={isStatusRestricted ? 'paused-lot' : ''}
                                                style={{ opacity: isStatusRestricted ? 0.5 : 1, cursor: isDisabledLot ? 'not-allowed' : 'pointer' }}
                                            >
                                                <td>
                                                    {lot.developmentLot ? (
                                                        <span className="lot-card-id demo">Demo</span>
                                                    ) : (
                                                        formatLotId(lot)
                                                    )}
                                                </td>
                                                <td>{lot.lotName}</td>
                                                <td>{lot.companyName || 'N/A'}</td>
                                                <td>{lot.address}</td>
                                                <td>
                                                    <DeviceStatusDots lotId={lot.lotId} developmentLot={lot.developmentLot} />
                                                </td>
                                                <td>{lot.createdOn ? formatDate(lot.createdOn) : 'N/A'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                    </>
                )}

                {totalItems > 0 && (
                    <Pagination
                        currentPage={page}
                        totalPages={Math.max(totalPages, 1)}
                        totalItems={totalItems}
                        itemsOnPage={currentItems.length}
                        onPageChange={goToPage}
                    />
                )}

            </div>

            {/* Account Modal */}
            <AccountModal isOpen={showAccountModal} onClose={() => setShowAccountModal(false)} />
        </div>
    );
};

export default LotDashboard;
